/**
 * `W5-T01` §4.3 — back from Stripe, the account page re-reads the status for a minute.
 *
 * Stripe's return *"does not mean all information was collected"*, and `account.updated` reaches us a
 * few seconds later, so the page asks again every five seconds while the status is one that waiting
 * can change, and stops after sixty. `features/messages/poll.ts`'s rule: the loader is what polls, and
 * a tick is skipped while the tab is hidden or the router is busy. A skipped tick still counts, so the
 * minute is a minute.
 *
 * `W5-T15` §3.2 watches a booking the same way, every two seconds, so the watch itself is `useWatch`
 * and takes its window.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigation, useRevalidator } from 'react-router';

export const WATCH_EVERY_MS = 5_000;
export const WATCH_FOR_MS = 60_000;

export interface WatchWindow {
  everyMs: number;
  forMs: number;
}

export function usePayoutsWatch(active: boolean): void {
  useWatch(active, { everyMs: WATCH_EVERY_MS, forMs: WATCH_FOR_MS });
}

/**
 * Re-run the route's loaders every `everyMs` while `active`, for `forMs` in all. Returns `false` once
 * the window has run out, so a page can say "still waiting" differently from "stopped waiting".
 */
export function useWatch(active: boolean, { everyMs, forMs }: WatchWindow): boolean {
  const revalidator = useRevalidator();
  const navigation = useNavigation();

  const busy = useRef(false);
  busy.current = revalidator.state !== 'idle' || navigation.state !== 'idle';
  const revalidate = useRef(revalidator.revalidate);
  revalidate.current = revalidator.revalidate;
  // Across effect runs, so a status that leaves and re-enters the watched set does not restart it.
  const ticksLeft = useRef(Math.floor(forMs / everyMs));
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!active || ticksLeft.current <= 0) return undefined;
    const timer = setInterval(() => {
      ticksLeft.current -= 1;
      if (ticksLeft.current < 0) {
        clearInterval(timer);
        setOpen(false);
        return;
      }
      if (busy.current || document.visibilityState !== 'visible') return;
      void revalidate.current();
    }, everyMs);
    return () => clearInterval(timer);
  }, [active, everyMs]);

  return open;
}
